import { SpecUploadFileFailure, SpecUploadResult } from "../types";

/**
 * 仕様書 ZIP 取り込みの結果を、利用者向けの通知内容へ変換する。
 *
 * 取り込みはファイル単位で失敗しても全体としては成功するため、応答を読まずに
 * 「アップロード完了」だけを出すと、取り込めなかったファイルがあることに
 * 気付けない（#164）。欠けがあるときは warning にして内訳を見せる。
 */

const REASON_LABELS: Record<string, string> = {
  load_error: "ファイルを読み取れませんでした",
  no_extractable_text:
    "本文を取り出せませんでした（画像のみの PDF など）",
  unsupported_extension: "対応していない形式です",
  excluded_name: "仕様書ではないファイルとして除外しました",
};

/** 表示するファイル名の上限。多いときは打ち切って残数を示す。 */
const MAX_LISTED_FILES = 5;

export interface SpecUploadNotification {
  type: "success" | "warning";
  title: string;
  message: string;
}

export function describeSpecUploadReason(reason: string): string {
  return REASON_LABELS[reason] ?? reason;
}

function listFiles(files: string[]): string {
  const shown = files.slice(0, MAX_LISTED_FILES);
  const rest = files.length - shown.length;
  const lines = shown.map((file) => `- ${file}`);
  if (rest > 0) {
    lines.push(`- ほか ${rest} 件`);
  }
  return lines.join("\n");
}

function listFailures(failures: SpecUploadFileFailure[]): string {
  const shown = failures.slice(0, MAX_LISTED_FILES);
  const rest = failures.length - shown.length;
  const lines = shown.map(
    (failure) => `- ${failure.file}: ${describeSpecUploadReason(failure.reason)}`
  );
  if (rest > 0) {
    lines.push(`- ほか ${rest} 件`);
  }
  return lines.join("\n");
}

export function buildSpecUploadNotification(
  result?: SpecUploadResult | null
): SpecUploadNotification {
  const failed = result?.failed_files ?? [];
  const summaryFailed = result?.summary_failed_files ?? [];
  const skipped = result?.skipped_files ?? [];
  const loadedCount = result?.file_count ?? result?.loaded_files?.length;

  const skippedNote =
    skipped.length > 0
      ? `\n${skipped.length} 件は仕様書ではないファイルとして対象外にしました。`
      : "";

  // 内訳を返さない応答（旧リビジョン）は従来どおり成功として扱う
  if (failed.length === 0 && summaryFailed.length === 0) {
    const countNote =
      loadedCount === undefined
        ? "仕様書ファイルをアップロードしました。"
        : `${loadedCount} 件のファイルを取り込みました。`;
    return {
      type: "success",
      title: "アップロード完了",
      message: `${countNote}${skippedNote}`,
    };
  }

  const sections: string[] = [];
  if (loadedCount !== undefined) {
    sections.push(`取り込めたファイル: ${loadedCount} 件`);
  }
  if (failed.length > 0) {
    sections.push(
      `取り込めなかったファイル（${failed.length} 件）:\n${listFailures(failed)}`
    );
  }
  if (summaryFailed.length > 0) {
    sections.push(
      `要約を作れなかったファイル（${summaryFailed.length} 件）:\n` +
        `${listFiles(summaryFailed)}\n` +
        "検索の対象には入りますが、一覧では要約が空欄になります。"
    );
  }
  if (skippedNote) {
    sections.push(skippedNote.trim());
  }

  return {
    type: "warning",
    title: "一部のファイルを取り込めませんでした",
    message: sections.join("\n\n"),
  };
}
